import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, reactive, toRefs } from "vue";
import { useRoute, useRouter } from "vue-router";

import RouteHelper from "@/common/route-helper";
import Search from "@/common/search";
import { QUERY_INFLECTION_SCHEME_ID_KEY } from "@/router/query-params";
import type { InflectionSchemeData } from "@/types";
import type { ArticleTab } from "@/types/search";

import { useSearchStore } from ".";

interface State {
  inflectionSchemeId: string;
  isInflectionSchemeOpen: boolean;
}

export const useInflectionSchemeStore = defineStore("inflection-scheme", () => {
  const state = reactive<State>({
    inflectionSchemeId: "__",
    isInflectionSchemeOpen: false
  });

  const route = useRoute();
  const router = useRouter();

  const searchStore = useSearchStore();

  const routeHelper = new RouteHelper(router);

  const inflectionSchemeData = computed<InflectionSchemeData | null>(() => {
    if (state.inflectionSchemeId.split("_").length !== 3) {
      return null;
    }

    const tab = (route.query.tab as ArticleTab | undefined) ?? "translations";

    for (const searchResult of searchStore.searchResult) {
      const inflectionSchemeData = Search.getInflectionSchemeData(
        state.inflectionSchemeId,
        searchResult,
        tab
      );

      if (inflectionSchemeData !== null) {
        return inflectionSchemeData;
      }
    }

    return null;
  });

  const inflectionSchemeLanguage = computed<"source" | "target">(() =>
    state.inflectionSchemeId.endsWith("__0") ? "source" : "target"
  );

  const openInflectionScheme = (inflectionSchemeId: string): void => {
    state.inflectionSchemeId = inflectionSchemeId;
    state.isInflectionSchemeOpen = true;
  };

  const closeInflectionScheme = (): void => {
    routeHelper.removeQueryParam(QUERY_INFLECTION_SCHEME_ID_KEY);

    state.inflectionSchemeId = "__";
    state.isInflectionSchemeOpen = false;
  };

  return {
    ...toRefs(state),

    inflectionSchemeData,
    inflectionSchemeLanguage,

    openInflectionScheme,
    closeInflectionScheme
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useInflectionSchemeStore, import.meta.hot));
}
